/* eslint-disable max-lines -- one translation catalog; splitting scatters shared strings */
import { plural, t } from '@lingui/core/macro';
import { NoddiTableTranslations, NoddiTabsTranslations, TablePaginationTranslations } from 'noddi-ui-web';
import {
  commonTexts,
  limitSummerTires,
  limitWinterTires,
  recommendedSummerTires,
  recommendedWinterTires,
  type WheelField
} from 'noddi-util';
import {
  FleetSwapTranslations,
  FleetViewColumnsTranslations,
  FleetViewFilterTranslations,
  type NoticeBannerTranslations,
  NotificationGroupOverviewTranslations,
  VinNumberDetailsTranslations
} from 'noddi-web';

const getTablePaginationTranslations = (): TablePaginationTranslations => ({
  rowsPerPage: t`Page size`,
  page: t`Page`,
  of: t`of`,
  selected: t`selected`,
  totalOf: t`Total of`,
  pageSize: t`Page size`,
  row: t`row(s)`,
  item: t`item(s)`,
  goToFirstPage: t`Go to first page`,
  goToPreviousPage: t`Go to previous page`,
  goToNextPage: t`Go to next page`,
  goToLastPage: t`Go to last page`,
  loadingMore: t`Loading more…`
});

export const getTableTranslations = (): NoddiTableTranslations => ({
  pagination: getTablePaginationTranslations(),
  filter: {
    filters: t`Filters`,
    resetAll: t`Reset all`,
    clearAll: t`Clear all`,
    showMore: (count: number) => plural(count, { one: `Show # more`, other: `Show # more` }),
    showLess: t`Show less`,
    noResultsFound: t`No results found...`,
    optionsLoadFailed: t`Could not load options`,
    retry: t`Try again`,
    retrying: t`Retrying…`,
    yes: t`Yes`,
    no: t`No`,
    done: t`Done`,
    filterDescription: t`Select filters to apply to the table`,
    appliedFilters: t`Applied filters`,
    noFiltersApplied: t`No filters are currently applied`,
    useFilterOptions: t`Use the filter options to narrow down your results`,
    filterCurrentlyApplied: (count: number) =>
      plural(count, { one: `# filter currently applied`, other: `# filters currently applied` }),
    search: (title: string) => t`Search ${title}...`,
    searchFilters: t`Search filters...`,
    noFiltersFound: t`No filters found`
  },
  view: {
    view: t`View`,
    columns: t`Columns`
  },
  contextMenu: {
    openMenu: t`Open menu`,
    copyRowId: t`Copy row id`,
    copiedToClipboard: t`Copied to clipboard`,
    failedToCopy: t`Failed to copy to clipboard`
  },
  emptyState: {
    noResults: t`No results`
  },
  columnHeader: {
    asc: t`Asc`,
    desc: t`Desc`,
    resetSorting: t`Reset sorting`
  },
  row: {
    keyboardHint: t`Press Enter to open`
  },
  rowSelectionBottomBar: {
    actions: t`Actions`,
    clearSelection: t`Clear selection`,
    itemsSelected: (count: number) => plural(count, { one: `# row selected`, other: `# rows selected` })
  }
});

export const getTabsTranslations = (): NoddiTabsTranslations => ({
  more: t`More`
});

export const getNoticeBannerTranslations = (): NoticeBannerTranslations => ({
  updatedAt: (when: string) => t`Updated ${when}`
});

export const getCommonTranslations = () => ({
  tireSelection: {
    perTire: t`Per tire`,
    total: t`Total`,
    pcs: t`pcs`,
    select: t`Select`,
    frontTire: t`Front tire`,
    rearTire: t`Rear tire`,
    frontTires: t`Front tires`,
    rearTires: t`Rear tires`,
    fuelEconomy: t`Fuel economy`,
    wetGrip: t`Wet grip`,
    noiseLevel: t`Noise level`,
    studdedTires: t`Studded tires`,
    euTireLabel: t`EU tire label`
  },
  ordersReceipts: {
    downloadReceipt: t`Download receipt`,
    noReceiptsFound: t`No receipts found for this order`
  },
  addressSearch: {
    noAddressesFound: t`No addresses found`,
    typeToSearchAddresses: t`Type to search addresses...`,
    searchForAddress: t`Search for address`
  },
  placeSearch: {
    placeholder: t`Search for a place...`,
    searchPlaceholder: t`Type to search places`,
    emptyMessage: t`No places found`
  },
  datePicker: {
    pickADate: t`Pick a date`
  },
  select: {
    placeholder: t`Select option`,
    searchPlaceholder: t`Search...`,
    emptyMessage: t`No option found`
  },
  free: t`Free`,
  chooseFromYourAddresses: t`Choose from your addresses?`,
  couldNotFindAddress: t`We couldn’t find the address. Please check for typos or missing details and try again!`,
  addressNotFound: t`No addresses found`,
  tryAgainWithStreetNumber: t`Please ensure the street number is included in the address`,
  chooseFromAnotherAddress: t`Choose from another address?`,
  noServiceOfferedNotice: t`Sorry, we don't deliver to this address yet`,
  somethingWentWrongWhileFetchingAddress: t`Something went wrong with fetching the addresses, please try again`,
  noStreetNumberNotice: t`Please enter an address with a street number so that we can check if we can come to you!`,
  pageNotFoundHeading: t`Page Not Found`,
  pageNotFoundMessage: t`The page you are looking for has either been moved or no longer exists. If we have linked incorrectly, we would be very happy if you send us a message.`,
  goBack: t`Go Back`,
  from: t`from`,
  hooray: t`Hooray!`,
  youCanBookTheseServicesToYourAddress: t`You can book these services to your address`,
  weDoNotOfferTheseServicesAtThisAddress: t`We do not offer these services at this address`,
  carDetails: {
    length: t`Length`,
    weight: t`Weight`,
    height: t`Height`,
    width: t`Width`,
    vinChassi: t`VIN/Chassi`,
    aboutTheCar: t`About the car`,
    model: t`Model`,
    nextCheck: t`Next check`,
    lastApproval: t`Last approval`,
    euCheckDetails: t`EU check`,
    firstRegisteredIn: t`First registered in`
  },
  infoCard: {
    edit: t`Edit`,
    editCard: (title: string) => {
      const name = title.toLowerCase();
      return t`Edit ${name}`;
    },
    add: (label: string) => {
      const name = label.toLowerCase();
      return t`Add ${name}`;
    },
    copy: (label: string) => {
      const name = label.toLowerCase();
      return t`Copy ${name}`;
    },
    copied: t`Copied`,
    copyFailed: t`Failed to copy to clipboard`,
    opensInNewTab: t`(opens in a new tab)`
  } satisfies typeof commonTexts.infoCard,
  vinNumberDetails: {
    title: t`VIN number details`,
    description: t`The VIN is a 17-character identifier. Each field below is one section of that number.`,
    wmi: t`World Manufacturer Identifier (WMI)`,
    wmiTooltip: t`Characters 1-3 of the VIN. Identifies the manufacturer and the country or region it is assigned to. Assigned by a national VIN authority under ISO 3779.`,
    vds: t`Vehicle Descriptor Section (VDS)`,
    vdsTooltip: t`Characters 4-8 of the VIN. Manufacturer-specific codes that describe the vehicle, typically model, body type, and engine.`,
    checkDigit: t`Check digit`,
    checkDigitTooltip: t`Character 9 of the VIN. Calculated from the other characters to catch typing errors. Often empty on European VINs, which do not always include a check digit.`,
    modelYear: t`Model year`,
    modelYearTooltip: t`Character 10 of the VIN. A single letter or number that encodes the model year on a repeating 30-year cycle. The decoded calendar year is shown in Year.`,
    assemblyPlant: t`Assembly plant`,
    assemblyPlantTooltip: t`Character 11 of the VIN. A manufacturer-assigned code for the factory where this vehicle was assembled.`,
    productionNumber: t`Production number`,
    productionNumberTooltip: t`Characters 12-17 of the VIN. Sequential serial number assigned by the manufacturer to uniquely identify this vehicle.`,
    formatType: t`Format type`,
    formatTypeTooltip: t`Which identifier scheme this number follows. EU VIN and US VIN are 17-character ISO 3779 numbers (US requires a check digit). Legacy chassis is an older identifier that is not a full VIN.`,
    year: t`Year`,
    yearTooltip: t`The decoded model year of the vehicle, derived from the model-year character. The same character repeats every 30 years, so this is the calendar year that best matches this car.`,
    euVin: t`EU VIN`,
    usVin: t`US VIN`,
    legacyChassis: t`Legacy chassis`
  } satisfies VinNumberDetailsTranslations,
  wheelMeasurements: {
    tires: t`Tires`,
    seeMeasurements: t`See measurements`,
    summerTires: t`Summer tires`,
    winterTires: t`Winter tires`,
    leftFront: t`Left front`,
    rightFront: t`Right front`,
    leftRear: t`Left rear`,
    rightRear: t`Right rear`,
    measuredAt: t`Measured at`,
    missingMeasurements: t`Missing measurements`,
    inAGoodCondition: t`In good condition`,
    soonWornOut: t`Soon worn out`,
    wornOut: t`Worn out`,
    treadDepthCondition: t`Tread depth condition`,
    treadDepth: t`Tread depth`,
    legalMinimum: t`Legal minimum`,
    recommendedMinimum: t`Recommended minimum`,
    allSeasonTires: t`All season tires`,
    unknown: t`Unknown`,
    showMore: t`Show more`,
    showLess: t`Show less`
  },
  notes: {
    attachments: t`Attachments`,
    openImage: (filename: string) => t`Open image ${filename}`,
    downloadDocument: (filename: string) => t`Download ${filename}`,
    removeAttachment: t`Remove attachment`
  },
  seasonBadge: {
    summerTires: t`Summer tires`,
    winterTires: t`Winter tires`,
    allSeasonTires: t`All Season tires`,
    unknown: t`Unknown tires`
  },
  add: t`Add`,
  getFleetSwapTexts: (supportEmail: string): FleetSwapTranslations => ({
    title: t`Swap car on schedule`,
    stepChooseCar: t`Choose car`,
    stepReview: t`Review`,
    stepDone: t`Done`,
    carLeavingSchedule: t`Car leaving the schedule`,
    replacementCar: t`Replacement car`,
    licensePlatePlaceholder: t`Search for reg. number`,
    invalidLicensePlate: t`Invalid license plate number`,
    searchHint: t`Cars in your fleet appear as you type. Enter a full license plate to look up a car that is not in your fleet.`,
    noOtherCars: t`You have no other cars that can take over this schedule.`,
    noMatchingCars: t`No cars in your fleet match your search.`,
    available: t`Available`,
    onSchedule: (enrolledIn: string) => t`On ${enrolledIn}`,
    selected: (incomingPlate: string) => t`${incomingPlate} selected`,
    selectCarToContinue: t`Select a car to continue`,
    cancel: t`Cancel`,
    continue: t`Continue`,
    back: t`Back`,
    confirmSwap: t`Confirm swap`,
    addCarAndSwap: t`Add car and swap`,
    close: t`Close`,
    notInFleet: (licensePlateNumber: string) => t`${licensePlateNumber} is not in your fleet`,
    lookUpHint: t`Look it up in the vehicle registry to add it to your fleet.`,
    lookUpCar: t`Look up car`,
    foundInRegistry: t`Found in registry`,
    addedOnConfirm: (plate: string) => t`${plate} is added to your fleet when you confirm the swap.`,
    cannotBeAdded: (plate: string) => t`${plate} cannot be added to your fleet`,
    ownedByOtherCustomer: (plate: string) =>
      t`${plate} is registered to another customer. Contact support at ${supportEmail} to move it to your fleet.`,
    alreadyOnSchedule: (plate: string) =>
      t`${plate} is already on a fleet schedule. Contact support at ${supportEmail} to move it.`,
    removed: t`Removed`,
    added: t`Added`,
    whatHappens: t`What happens when you confirm`,
    outgoingLeaves: (outgoingPlate: string, scheduleName: string) =>
      t`${outgoingPlate} leaves "${scheduleName}", and its upcoming bookings on this schedule are canceled. It stays in your fleet.`,
    addedToFleet: (incomingPlate: string) => t`${incomingPlate} is added to your fleet.`,
    incomingJoins: (incomingPlate: string, scheduleName: string) =>
      t`${incomingPlate} joins "${scheduleName}" and is picked up by the next scheduled service.`,
    nowOnSchedule: (incomingPlate: string, scheduleName: string) => t`${incomingPlate} is now on "${scheduleName}"`,
    noLongerOnSchedule: (outgoingPlate: string) =>
      t`${outgoingPlate} is no longer on the schedule and stays in your fleet.`,
    canceledBookings: (canceledBookingCount: number, outgoingPlate: string) =>
      plural(canceledBookingCount, {
        one: `# upcoming booking for ${outgoingPlate} was canceled.`,
        other: `# upcoming bookings for ${outgoingPlate} were canceled.`
      })
  }),
  getRegNumberText: () => ({
    add: t`Add`,
    placeholder: t`Search for reg. number`,
    invalidLicensePlate: t`Invalid license plate number`,
    alreadyExists: t`The car with this license plate number has already been added`
  }),
  myCars: t`My cars`,
  noCarsAssignedToYou: t`You are not the driver of any cars yet. Your organization's administrator chooses who drives each car.`,
  amount: t`Amount`,
  discount: t`Discount`,
  used: t`Used`,
  activated: t`Activated`,
  enterCode: t`Enter code`,
  confirm: t`Confirm`,
  standardsForWinterAndSummerTires: t`Standards for winter and summer tires`,
  winterTiresMinimumNotice: t`In Norway, the minimum requirement for tread depth on winter tires is 3 mm. It is illegal to drive with winter tires that are under 3 mm. We recommend changing tires when the tread depth is below 4 mm. When the tread depth is below 4 mm, the braking distance increases considerably, and the tire loses its performance.`,
  summerTiresMinimumNotice: t`In Norway, the minimum requirement for tread depth on summer tires is 1.6 mm. It is illegal to drive with summer tires under 1.6 mm. We recommend changing tires when the tread depth is below 3 mm. When the tread depth is below 3 mm, the braking distance increases considerably, and the tire loses its performance.`,

  smsLoginTranslations: {
    phoneNumber: t`Phone number`,
    enterNumberToReceiveVerificationCode: t`Enter number to receive a verification code`,
    resend: t`Resend`,
    sendCode: t`Send code`,
    didntReceiveSms: t`Didn't receive sms?`,
    enterCode: t`Enter verification code`,
    confirm: t`Confirm`,
    logIn: t`Log in`,
    sentTo: t`Sent to`,
    changeNumber: t`Change number`,
    sendNewCode: t`Send new code`,
    accessDeniedLoginMessage: t`Access denied. If you believe this is a mistake or require permission, please contact support`,
    chooseAccount: t`Choose an account`,
    chooseAccountDescription: t`This phone number is linked to more than one account. Select the one you want to use.`,
    back: t`Back`,
    personalAccountPrivate: t`Private`,
    personalAccountBusiness: t`Business`
  },
  emailLoginTranslations: {
    emailAddress: t`Email`,
    password: t`Password`,
    logIn: t`Log in`,
    accessDeniedLoginMessage: t`Access denied. If you believe this is a mistake or require permission, please contact support`,
    forgotPassword: t`Forgot password?`,
    passwordResetSuccess: (email: string) => t`We've sent a link to reset your password to ${email}.`,
    invalidEmail: t`Must provide a valid email address`,
    passwordRequired: t`Password is required`,
    passwordReset: {
      resetPassword: t`Reset password`,
      enterEmailToReset: t`Enter your email address to receive a password reset link`,
      sendResetLink: t`Send reset link`,
      cancel: t`Cancel`,
      email: t`Email`,
      invalidEmail: t`Must provide a valid email address`,
      resetLinkSent: t`Reset link sent!`,
      checkEmailForLink: (email: string) =>
        t`We've sent a link to reset your password to ${email}. If it doesn't arrive within a few minutes, check your spam folder.`,
      linkExpiresIn: t`The link expires in`,
      linkExpired: t`The link has expired. Request a new one to continue.`,
      passwordResetSuccessful: t`Password reset successful`,
      passwordResetSuccessfulMessage: t`Password reset successful! You can now sign in with your new password.`,
      backToLogin: t`Back to login`
    }
  },
  registrationTranslations: {
    thisFieldIsRequired: t`This field is required`,
    thisFieldMustBeAtLeast2CharactersLong: t`This field must be at least 2 characters long`,
    mustProvideAValidEmailAddress: t`Must provide a valid email address`,
    passwordsMustMatch: t`Passwords must match`,
    thePasswordMustBeAtLeast8CharactersLong: t`The password must be at least 8 characters long`,
    firstName: t`First name`,
    surname: t`Surname`,
    email: t`Email`,
    password: t`Password`,
    confirmPassword: t`Confirm password`,
    createUser: t`Create account`,
    verificationExpired: t`Your phone verification has expired. Verify your number again to continue. Your details will be kept.`,
    verifyPhoneAgain: t`Verify phone number again`,
    userInformation: t`Please fill in your information`,
    licensePlateNumber: t`License plate number (optional)`,
    addLicensePlate: t`Add license plate`,
    removeLicensePlate: t`Remove license plate`,
    possibleExistingAccount: t`These details match an existing account. A new account will not sign you into it.`,
    createNewAccount: t`Create new account`,
    emailAlreadyHasAccount: t`This email already has an account. Sign in with your password, or reset it.`,
    emailSignInLinkPrompt: t`We found an account for this email. We can send a sign-in link to it.`,
    sendSignInLink: t`Email me a sign-in link`,
    signInLinkSent: t`Check your email for a link to sign in.`,
    signIn: t`Sign in`,
    forgotPassword: t`Forgot password?`,
    passwordResetSuccess: (email: string) => t`We've sent a link to reset your password to ${email}.`,
    accessDeniedLoginMessage: t`Access denied. If you believe this is a mistake or require permission, please contact support`
  },

  profileUpdatedSuccessfully: t`Profile updated successfully`,
  firstName: t`First name`,
  lastName: t`Last name`,
  phoneNumber: t`Phone number`,
  save: t`Save`,
  email: t`Email`,
  required: t`Required`,
  contactVerification: {
    codeSent: t`Confirmation code sent`,
    emailVerificationCode: t`Email confirmation code`,
    phoneNumberVerificationCode: t`SMS confirmation code`,
    sendEmailCode: t`Send email code`,
    sendSmsCode: t`Send SMS code`
  },
  // Backward compatible flat format for TablePagination translations
  table: {
    rowsPerPage: t`Page size`,
    page: t`Page`,
    of: t`of`,
    selected: t`selected`,
    totalOf: t`Total of`,
    pageSize: t`Page size`,
    row: t`row(s)`,
    item: t`item(s)`,
    goToFirstPage: t`Go to first page`,
    goToPreviousPage: t`Go to previous page`,
    goToNextPage: t`Go to next page`,
    goToLastPage: t`Go to last page`,
    clearFilters: t`Clear filters`
  },
  orderSummary: {
    total: t`Total`,
    discount: t`Discount`,
    VAT: t`VAT`,
    vat: t`VAT`,
    other: t`Other`
  },
  priceListDiscountLabel: {
    withName: (priceListName: string, percentText: string) => t`${priceListName}, ${percentText}% off`,
    percentOnly: (percentText: string) => t`${percentText}% off`
  },
  priceListContextBanner: {
    withPercent: (percentText: string) => t`Your prices include a ${percentText}% discount.`,
    agreementOnly: t`Your prices follow this agreement.`,
    activeLabel: t`Active`
  },
  tireHotelAtOtherDepartmentDialog: {
    title: t`Tire hotel at another location`,
    description: (departmentName: string) =>
      t`This car has a tire hotel at ${departmentName}. Book there so the stored wheels are at the same location as the service.`,
    switchTo: (departmentName: string) => t`Switch to ${departmentName}`,
    continueHere: t`Continue here`
  },
  errorPage: {
    title: t`We're sorry, an error has occurred`,
    tryAgain: t`Try again`
  },
  timePicker: {
    today: t`Today`,
    tomorrow: t`Tomorrow`,
    soldOut: t`Sold out`,
    nextButton: t`Later`,
    prevButton: t`Earlier`,
    full: t`Full`,
    // "open", not "available": a phone day tab is a fifth of the row, and "2 available" ran into the next tab.
    availableCount: (count: number) => plural(count, { one: `# open`, other: `# open` }),
    loading: t`Loading`,
    jumpToDateTriggerText: t`Go to date`,
    earliestDeliveryDialogTitle: t`Can't go back further`,
    earliestDeliveryDialogMessage: t`This is the earliest date we can come to you`,
    earliestAvailableDate: t`Earliest available date`,
    earliestDeliveryReasons: {
      wheel_storage_inventory_lead_time: t`Your tires are in storage and need time to be prepared before delivery.`,
      wheel_pickup_at_other_address_lead_time: t`Your tires are at another address and need time to be collected before delivery.`,
      default: t`We're working hard to fit you in as soon as possible!`
    },
    latestDeliveryDialogTitle: t`Can't go forward further`,
    latestDeliveryDialogMessage: t`This is the latest date currently open for booking.`,
    latestAvailableDate: t`Latest available date as of now`,
    latestDeliveryExplanation: t`Later dates will open up as we get closer. Check back soon for more options!`
  },

  discounts: t`Discounts`,
  other: t`Other`,
  VAT: t`VAT`,
  total: t`Total`,
  language: t`Language`,
  inspectionCard: {
    title: t`Wheel inspection data`,
    dimensions: t`Dimensions`,
    frontTires: t`Front tires`,
    rearTires: t`Rear tires`,
    inspectionDate: t`Inspection date`,
    inspectedBy: t`Inspected by`,
    beforeService: t`Before service`,
    afterService: t`After service`,
    standalone: t`Standalone`,
    noPhoto: t`No photo yet`
  },
  fleetView: {
    columnTranslations: {
      car: t`Car`,
      driver: t`Driver`,
      customer: t`Customer`,
      wheelSetAtTireHotel: t`Wheel set at tire hotel`,
      none: t`None`,
      lastBooking: t`Last booking`,
      upcomingBooking: t`Upcoming booking`,
      summerTires: t`Summer tires`,
      winterTires: t`Winter tires`,
      noMeasurements: t`No measurements`,
      mustBeReplaced: t`Must be replaced`,
      shouldBeReplaced: t`Should be replaced`,
      goodCondition: t`Good condition`,
      allSeasonTires: t`All Season`,
      unknownSeason: t`Unknown`,
      cancelled: t`Cancelled`,
      ownerSince: (date: string) => t`Owner since ${date}`,
      currentOwners: (count: number) => plural(count, { one: `# current owner`, other: `# current owners` }),
      leased: t`Leased`,
      fleetSchedule: t`Fleet schedule`
    } satisfies FleetViewColumnsTranslations,
    filterTranslations: {
      hasUpcomingBooking: t`Upcoming booking`,
      hasUpcomingBookingDescription: t`Specifies if the car has a booking that is not completed or canceled`,
      upcomingBookingDateRange: t`Upcoming booking date range`,
      upcomingBookingDateRangeDescription: t`Filter by the date of the car's next upcoming booking`,
      lastBookingDateRange: t`Last booking date range`,
      lastBookingDateRangeDescription: t`Filter by the date of the car's last completed booking`,
      wheelSetInStorage: t`Wheel set in storage`,
      wheelSetInStorageDescription: t`Filter by which tire season is currently stored in the tire hotel`,
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded
      winter: t`❄️ Winter`,
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded
      summer: t`☀️ Summer`,
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded
      none: t`🚫 None`,
      carMakes: t`Car makes`,
      carMakesDescription: t`Filter by specific car makes`,
      carModels: t`Car models`,
      carModelsDescription: t`Filter by specific car models`,
      summerTireCondition: t`Summer tire condition`,
      winterTireCondition: t`Winter tire condition`,
      mustBeReplaced: t`Must be replaced`,
      noMeasurements: t`No measurements`,
      shouldBeReplaced: t`Should be replaced`,
      goodCondition: t`Good condition`,
      tireWearInfo: {
        summerTitle: t`Summer tire wear information`,
        winterTitle: t`Winter tire wear information`,
        goodCondition: t`Good condition`,
        shouldBeReplaced: t`Should be replaced`,
        mustBeReplaced: t`Must be replaced`,
        regulationsTitle: t`Regulations and our recommendations`,
        summerRegulationsDescription: t`In Norway, the minimum requirement for tread depth on summer tires is ${limitSummerTires} mm. It is illegal to drive with summer tires that are under ${limitSummerTires} mm. We recommend changing tires when the tread depth is below ${recommendedSummerTires} mm. When the tread depth is below ${recommendedSummerTires} mm, the braking distance increases considerably, and the tire loses its performance.`,
        winterRegulationsDescription: t`In Norway, the minimum requirement for tread depth on winter tires is ${limitWinterTires} mm. It is illegal to drive with winter tires that are under ${limitWinterTires} mm. We recommend changing tires when the tread depth is below ${recommendedWinterTires} mm. When the tread depth is below ${recommendedWinterTires} mm, the braking distance increases considerably, and the tire loses its performance.`
      }
    } satisfies FleetViewFilterTranslations
  },
  notificationGroupOverview: {
    title: t`Members and drivers`,
    description: t`Administrators see all of the organization's cars and bookings and manage its members. Other members see only the cars they drive and the bookings they made or that include those cars. Each car has one driver who receives SMS about it, and a member can be the driver of several cars.`,
    byCar: t`Cars`,
    byCarDescription: t`Who drives each car. A car without a driver gets no SMS.`,
    byMember: t`Members`,
    byMemberDescription: t`Each member's role, contact details and the cars they drive.`,
    car: t`Car`,
    member: t`Member`,
    driver: t`Driver`,
    driverTooltip: t`The driver receives SMS notifications about this car and can see it and its bookings. Their settings determine whether they receive booking updates, service reminders, or both.`,
    drives: t`Cars they drive`,
    drivesTooltip: t`The cars this member is the driver of. They receive SMS about these cars and can see them and their bookings.`,
    otherCars: t`Other cars`,
    searchCarsTablePlaceholder: t`Search by car or driver`,
    searchMembersTablePlaceholder: t`Search by name, phone or email`,
    noDriver: t`No driver`,
    peopleCount: (count) => plural(count, { one: '1 person', other: '# people' }),
    carCount: (count) => (count === 0 ? t`No cars` : plural(count, { one: '1 car', other: '# cars' })),
    bookingSms: t`Booking updates`,
    serviceReminderSms: t`Service reminders`,
    bookingSmsDescription: t`Confirmation, arrival time and completion of bookings for their cars.`,
    serviceReminderSmsDescription: t`A heads-up when one of their cars is due for a seasonal wheel change.`,
    smsTurnedOffWarning: t`Both SMS types are off, so this member gets no messages about the cars they drive.`,
    smsTurnedOff: t`SMS turned off`,
    carsWithoutDriver: (count) => plural(count, { one: '1 car has no driver', other: '# cars have no driver' }),
    carsWithoutDriverDescription: t`No one receives SMS notifications about these cars. Choose a driver in the Cars tab.`,
    carsWithSilencedDriver: (count) =>
      plural(count, {
        one: '1 car has a driver with SMS notifications turned off',
        other: '# cars have a driver with SMS notifications turned off'
      }),
    carsWithSilencedDriverDescription: t`Turn on the driver's SMS notifications or choose another driver.`,
    editDriver: t`Change who drives this car`,
    driverSheetDescription: t`Only the driver receives SMS about this car. Nothing is saved until you confirm.`,
    searchMembersPlaceholder: t`Search by name or phone number`,
    noMembersFound: t`No members found.`,
    otherMembers: t`Other members`,
    driverSaved: (licensePlate) => t`Driver for ${licensePlate} updated`,
    moreCars: (count) => t`+${count} more`,
    noDrivenCars: t`Drives no cars yet`,
    noDrivenCarsDescription: t`Choose cars to make this member their driver. Only the driver receives SMS about a car.`,
    back: t`Back`,
    addCarsDescription: (name) => t`Choose the cars ${name} should drive. Nothing is saved until you confirm.`,
    searchCars: t`Search by make, model or license plate`,
    noCarsFound: t`No cars found.`,
    currentDriver: (name) => t`Current driver: ${name}`,
    currentDriverLabel: t`Current driver`,
    cancel: t`Cancel`,
    addMember: t`Add member`,
    addMemberStepNumber: t`Number`,
    addMemberStepNumberDescription: t`Who to add`,
    addMemberStepPerson: t`Person`,
    addMemberStepPersonDescription: t`Confirm or create`,
    addMemberStepSettings: t`Settings`,
    addMemberStepSettingsDescription: t`Role and SMS`,
    next: t`Continue`,
    memberFoundDescription: t`Check that this is the right person before adding them.`,
    memberAlreadyInGroup: t`This person is already a member of the organization.`,
    memberUnavailable: t`This number belongs to an account that cannot be added. Contact support.`,
    memberNotFound: t`No account has this number yet. Fill in their name to create one.`,
    memberEmailTaken: t`This email already belongs to another account. Leave it blank or use a different one.`,
    emailOptional: t`Email (optional)`,
    sendAddedSms: t`Tell them they were added`,
    sendAddedSmsDescription: t`One SMS now, with a link to log in. Not a notification setting.`,
    memberAdded: (name) => t`${name} added`,
    memberEditAdminOnly: t`Only an administrator can change another member's details.`,
    roleAndDriversAdminOnly: t`Only an administrator can change roles and drivers.`,
    adminEditsRest: t`An administrator changes the rest.`,
    viewOnly: t`View only`,
    role: t`Role`,
    groupAdmin: t`Administrator`,
    groupAdminDescription: t`Sees all of the organization's cars, bookings and members' contact details. Can add and remove members, choose who drives which car, and change each member's SMS notifications. Members without this role see only the cars they drive and the bookings they made or that include those cars.`,
    holderGroupAdminDescription: t`Can add and remove members and change each member's SMS notifications. An administrator of this organization is also an employee.`,
    employee: t`Employee`,
    employeeDescription: t`Can open this organization in the customer app and book the company's own cars. Cars leased to a customer stay on that customer's account.`,
    driverEditAdminOnly: t`Only an administrator can choose who drives which car.`,
    lastAdminHint: t`An organization needs at least one administrator.`,
    makeAnotherAdminFirst: t`Make someone else an administrator before you leave.`,
    accountOwnedByMember: (name) => t`Only ${name} can change their own contact details.`,
    contactDetails: t`Contact details`,
    firstName: t`First name`,
    lastName: t`Last name`,
    phoneNumber: t`Phone number`,
    email: t`Email`,
    notifications: t`SMS notifications`,
    notificationsDescription: t`Sent only for the cars this member drives.`,
    saveChanges: t`Save changes`,
    edit: t`Edit`,
    memberSaved: (name) => t`${name} updated`,
    removeMember: t`Remove member`,
    removeMemberDescription: (name) => t`Remove ${name} from this organization? Their account is not affected.`,
    leaveGroup: t`Leave organization`,
    leaveGroupDescription: t`You lose access to the cars and bookings in this organization. Your account is not affected.`,
    remove: t`Remove`,
    memberRemoved: (name) => t`${name} removed from the organization`,
    primaryContactCannotBeRemoved: t`We contact this person on behalf of the organization, so they cannot be removed. Contact support to point that at someone else first.`,
    primaryContactTitle: t`Primary contact`,
    primaryContactDescription: t`The person we contact on behalf of the organization when we need to reach the group. There is one primary contact for the whole organization.`,
    primaryContactSaved: (name) => t`${name} is now the primary contact`,
    chooseDriverStep: t`Step 1 of 2 · Choose the new driver`,
    chooseCarsStep: t`Step 1 of 2 · Choose the cars`,
    reviewStep: t`Step 2 of 2 · Review and confirm`,
    continueToReview: t`Continue`,
    confirmDriverChange: t`Confirm change`,
    takingOverFrom: (name) => t`Taking over from ${name}`,
    alreadyDrivesCount: (count) => {
      const cars = plural(count, { one: '1 car, which stays as it is', other: '# cars, which stay as they are' });
      return t`Already drives ${cars}.`;
    },
    whatHappens: t`What happens when you confirm`,
    bothSmsTypes: t`booking updates and service reminders`,
    bookingSmsInline: t`booking updates`,
    serviceReminderSmsInline: t`service reminders`,
    effectIncomingReceives: (name, smsTypes, cars) => t`${name} gets ${smsTypes} for ${cars}.`,
    effectIncomingSilenced: (name, cars) =>
      t`${name} has SMS turned off, so nobody is notified about ${cars} until it is turned back on.`,
    effectIncomingGainsAccess: (name, cars, carCount) =>
      carCount === 1
        ? t`${name} can now see ${cars} and its bookings.`
        : t`${name} can now see ${cars} and their bookings.`,
    effectOutgoingStops: (name, cars) => t`${name} stops getting SMS about ${cars}.`,
    effectOutgoingLosesAccess: (name, cars) =>
      t`${name} can no longer see ${cars}, apart from bookings they made themselves.`,
    effectOutgoingRemaining: (name, count) => {
      const cars = plural(count, { one: '1 other car', other: '# other cars' });
      return t`${name} still drives ${cars}.`;
    },
    effectOutgoingNoneLeft: (name) => t`${name} drives no cars after this.`,
    effectNoPreviousDriver: (cars) => t`${cars} has no driver today, so nobody loses notifications.`,
    effectMembershipUnchanged: t`Everyone keeps their membership and their own SMS settings.`,
    turnOnSmsForMember: (name) => t`Turn on SMS for ${name}`,
    driversSaved: (count) => plural(count, { one: 'Driver updated', other: 'Drivers updated for # cars' }),
    addCars: t`Add cars`,
    showCarsNeedingDriver: t`Show these cars`,
    showAllCars: t`Show all cars`,
    filteredToCarsNeedingAttention: t`Showing only cars that need attention`,
    handoverCars: (name, carCount) => {
      const cars = plural(carCount, { one: '1 car', other: '# cars' });
      return t`${name} drives ${cars}. Those cars move to another member.`;
    },
    handoverProposals: (name, proposalCount) =>
      plural(proposalCount, {
        one: `${name}'s open service reminder goes to whoever takes over.`,
        other: `${name}'s # open service reminders go to whoever takes over.`
      }),
    leaveHandoverCars: (carCount) => {
      const cars = plural(carCount, { one: '1 car', other: '# cars' });
      return t`You drive ${cars}. Those cars move to another member.`;
    },
    leaveHandoverProposals: (proposalCount) =>
      plural(proposalCount, {
        one: 'Your open service reminder goes to whoever takes over.',
        other: 'Your # open service reminders go to whoever takes over.'
      }),
    moveCarsTo: t`Move cars to`,
    chooseMember: t`Choose a member`,
    noOtherMembers: t`Nobody else is in this organization. Add a member first.`,
    removeCar: t`Remove car`,
    removeCarTitle: t`Remove car from this organization?`,
    removeCarDescription: (carName) => t`Remove ${carName} from this organization? Past bookings stay as they are.`,
    removeCarReason: t`Reason`,
    selectReason: t`Select a reason`,
    reasonSold: t`Sold`,
    reasonWrecked: t`Wrecked / scrapped`,
    reasonNeverOwned: t`Never owned (not mine)`,
    reasonOther: t`Other`,
    removeCarNote: t`Note`,
    removeCarNotePlaceholder: t`Why this car is leaving the organization`,
    carRemoved: (carName) => t`${carName} removed from the organization`,
    pagination: getTablePaginationTranslations()
  } satisfies NotificationGroupOverviewTranslations
});

/** The wheel labels an inspection card shows, in the customer's language. */
export const getWheelLabels = (): Record<WheelField, string> => {
  const trans = getCommonTranslations().wheelMeasurements;
  return {
    frontLeft: trans.leftFront,
    frontRight: trans.rightFront,
    rearLeft: trans.leftRear,
    rearRight: trans.rightRear
  };
};
